// @ts-ignore
import { ANSWER_STATUSES } from '@yandex-int/k-common/constants';
import { Resource, VerdictOverrideConfigFiltersToFind } from '@yandex-int/k-common/typings';
import { ReactNode } from 'react';

import { AnswerCommit, SingleAnswer } from '../problem/types';

import { RobotLightNumberOfProcedures, ROBOT_LIGHT_PROCEDURE } from './__code-light/robot__code-light.types';
import {
    ROBOT_CELL_TYPE,
    ROBOT_COMMAND_TYPE,
    ROBOT_COORD,
    ROBOT_FINISH_STATUS,
    ROBOT_LINE_NUMBER,
} from './robot.constants';

export interface RobotPropsPreview {
    previewMode: true;
    problem: RobotProblem;
}

export interface RobotProps {
    previewMode?: boolean; // false;
    problem: RobotProblem;
    children?: ReactNode;
    readOnly?: boolean; // мб 'evaluated'
    initialTab?: ROBOT_TABS;
    onControlsClick?: (buttonSlug: string) => void;
    postRunCode?: (params: { problemId: number; markers: { user_answer: string } }) => Promise<RobotRunCodeResponse>;
    attempt?: RobotAttempt;
    answer?: SingleAnswer<UserAnswer>;
    setAnswer?: (answer: AnswerCommit<UserAnswer>) => void;
    showCorrectAnswers?: boolean;
    showCurrentAnswersStatus?: boolean;
    dispatch?: (action: unknown) => unknown;
    teacherMode?: boolean;
    isEvaluable?: boolean;
    evaluated?: boolean;
    submitAnswer?: () => void;
    feedback?: {
        show: () => void;
        hide: () => void;
    };
}

interface RobotProblemOnboarding {
    src: string;
    description?: string;
    autoshow?: boolean;
}

// Задача
export interface RobotProblem {
    id: number;
    type: 'robot';

    // Разметка задачи
    markup: RobotMarkup;

    // Описание ресурсов, которые могут
    // использоваться для оформления поля робота
    resources: Record<number, Resource>;

    onboardings?: [RobotProblemOnboarding | undefined];

    // поля обычного проблема останутся?
    // date_updated: UnixTime | null;
    // meta: ProblemMeta;
    // subject: SUBJECTS_SLUGS;
    // owner: number;
    // max_points: number;
    // custom_answer: boolean;
    // background_cover?: string;
    // background_color?: string;
}

type Samples = Record<number, RobotCommand[]>;

// Разметка задачи
interface RobotMarkup {
    interactor: string;
    options: {
        // Заголовок задачи, plain text
        instruction: string;

        // Условие задачи, markdown
        description: string;

        // Массив функций робота, доступных для автодополнения в редакторе
        // ['move_left', 'move_right', 'wall_from_up']
        function_hints?: string[];

        // Скин - оформление поля
        skin?: RobotSkin;
    };

    // Авторское решение, ученику приходит после появления доступа
    // поля постепенно переезжают в code_editors
    author_solution?: string;
    author_solution_run?: Samples;

    code_editors?: Editors[];

    // Тесты
    samples: Samples;
    sample_number: number;
    hidden_samples: number[];

    cm_comment?: string;
}

export type PythonCodeString = string;

type BlocklyXMLString = string;

interface LightPythonBlock {
    id: string;
    name: string;
}

interface CodeLightV2Block {
    id: string;
    name: string;
}

// eslint-disable-next-line no-shadow
export enum EDITOR_TYPES {
    PYTHON = 'python',
    BLOCKLY_PYTHON = 'blockly_python',
    LIGHT_PYTHON = 'light_python',
    CODE_LIGHT_V2 = 'code_light_v2',
}

interface AnswerWithCode {
    code: string;
}

export interface PythonAnswer extends AnswerWithCode {
    editor: EDITOR_TYPES.PYTHON;
}

export interface BlocklyPythonAnswer extends AnswerWithCode {
    editor: EDITOR_TYPES.BLOCKLY_PYTHON;
    blocks: BlocklyXMLString;
}

interface LightPythonAnswer extends AnswerWithCode {
    editor: EDITOR_TYPES.LIGHT_PYTHON;
    blocks?: LightPythonBlock[];
}

interface CodeLightV2Answer extends AnswerWithCode {
    editor: EDITOR_TYPES.CODE_LIGHT_V2;
    blocks?: CodeLightV2Block[];
}

export interface ObjectAnswer<Answer = PythonAnswer | BlocklyPythonAnswer | LightPythonAnswer | CodeLightV2Answer> {
    version: '1';
    answer: Answer;
}

export type UserAnswer = PythonCodeString | ObjectAnswer;

interface PythonEditor {
    editor: EDITOR_TYPES.PYTHON;
    author_solution: Omit<PythonAnswer, 'editor'>;
    author_solution_run?: Samples;
}

export interface BlocklyPythonEditor {
    editor: EDITOR_TYPES.BLOCKLY_PYTHON;
    author_solution: Omit<BlocklyPythonAnswer, 'editor'>;
    author_solution_run?: Samples;
    available_blocks?: string[];
}

interface LightPythonEditor {
    editor: EDITOR_TYPES.LIGHT_PYTHON;
    author_solution: Omit<LightPythonAnswer, 'editor'>;
    author_solution_run?: Samples;
}

interface CodeLightV2Editor {
    editor: EDITOR_TYPES.CODE_LIGHT_V2;
    author_solution: Omit<CodeLightV2Answer, 'editor'>;
    author_solution_run?: Samples;
}

export type Editors = PythonEditor | BlocklyPythonEditor | LightPythonEditor | CodeLightV2Editor;

export interface RobotAttempt {
    markers: {
        user_answer: UserAnswer;
        result: Record<number, RobotAttemptItem>;
        error?: string;
    };
    completed?: boolean;
    status?: ANSWER_STATUSES;
    // answered: boolean;
    // spent_time: boolean;
    // custom_answer: boolean;
    // points: boolean;
    // comment: boolean;
}

type RobotColor = string; // #fff, rgb(), hsl(), ..

export interface RobotSkinResource {
    resource_id?: Resource['id'];
    resource_url?: NonNullable<Resource['file']>;
}

export type RobotCommandConfig = { name: string; label: string; iconClassName?: string };
export interface RobotSkinSample {
    color?: RobotColor;
    border?: string;
    backgrounds?: RobotSkinResource[];
    foregrounds?: RobotSkinResource[];
    robot_view?: 'robert' | 'rover';
    robot_view_url?: string;
    max_commands?: number;
    hide_grid?: boolean;
    sprites?: {
        robot?: RobotSkinResource;
        start?: RobotSkinResource;
        finish?: RobotSkinResource;
        // 'labels:*': RobotSkinResource;
        // 'colors:0': RobotSkinResource;
        [key: string]: RobotSkinResource | undefined;
    };
    grid?: RobotSkinResource & {
        width?: number; // Ширина линий сетки
        color?: RobotColor; // not supported
    };
    walls?: {
        width?: number;
        color?: RobotColor;
    };
    commands_options?: RobotCommandsOptions;
    commands?: {
        move: RobotCommandConfig[];
        fill: RobotCommandConfig[];
    };
    sprites_options?: RobotSpritesOptions;
}

export type RobotCommandsOptions = { [C in RobotCommandName]?: RobotCommandOptions };

export type RobotCommandName = string;

export interface RobotCommandOptions {
    background_color?: string;
    icon?: string;
    label?: string;
    shadow_color?: string;
}

export type RobotSpritesOptions = Record<RobotSpriteName, RobotSpriteOptions>;

export type RobotSpriteName = string;

export interface RobotSpriteOptions {
    background_size?: string;
}

export interface RobotSkin {
    page?: RobotSkinResource & {
        size?: string;
        color: RobotColor;
    };
    samples?: {
        default?: RobotSkinSample;
        [key: number]: RobotSkinSample | undefined;
        onboarding?: {
            src?: string;
            autoshow?: boolean;
            description?: string;
        };
    };
    hide_finish?: boolean;
    number_of_procedures?: RobotLightNumberOfProcedures;
    max_commands_in_procedures?: {
        [procedure_name in ROBOT_LIGHT_PROCEDURE]?: number;
    };
    verdict_override_filters?: VerdictOverrideConfigFiltersToFind;
}

// Тест
export interface RobotSampleVisible {
    index: number;

    hidden: false;

    // Описание поля теста, массив команд робота
    field: RobotCommand[];

    // дополнить разметку для скина
    skin?: {
        background_image?: number; // подложка поля
        foreground_image?: number; // объекты на поле
    };
}

// Скрытый Тест
export interface RobotSampleHidden {
    index: number;

    hidden: true;
}

// Вспомогательный тип описания поля робота в который переводятся поля samples, sample_number, hidden_samples
export type RobotSample = RobotSampleVisible | RobotSampleHidden;

export type RobotRunCode = () => Promise<RobotAttempt['markers']>;

export interface RobotCheckCodeRequest {
    // могут быть в url
    problemLinkId: number;
    resultId: number;

    // id последней отправленной попытки
    lastAttemptId: number;

    // Код для проверки
    code: string;

    // что-то нужно еще из того, что есть в patch clesson results?
    // answer: string;
    // answered: boolean;
    // completed: boolean;
    // clessonId: number;
    // isEvaluable: boolean;
    // manualAnswer: CustomAnswer;
}

export interface RobotPosition {
    [ROBOT_COORD.HORIZONTAL]: number;
    [ROBOT_COORD.VERTICAL]: number;
}

interface RobotCommandBegin {
    type: ROBOT_COMMAND_TYPE.BEGIN;
}

export interface RobotCommandCreateField extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.CREATE_FIELD;
}

interface RobotCommandCreateWalls extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.CREATE_WALLS;
    sides: number;
}

interface RobotCommandSetCellType extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.SET_CELL_TYPE;
    cell_type: ROBOT_CELL_TYPE;
}

export interface RobotCommandSetCellColor extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.SET_CELL_COLOR;
    color: number;
    [ROBOT_LINE_NUMBER]?: number;
}

interface RobotCommandSetRequiredColor extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.SET_REQUIRED_COLOR;
    color: number;
}

interface RobotCommandSetTemperature extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.SET_TEMPERATURE;
    temperature: number;
}

interface RobotCommandSetRadiation extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.SET_RADIATION;
    radiation: number;
}

interface RobotCommandCreateLabel extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.CREATE_LABEL;
    label: string;
}

interface RobotCommandCreateParkingPoint extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.CREATE_PARKING_POINT;
}

interface RobotCommandFinishInit {
    type: ROBOT_COMMAND_TYPE.FINISH_INIT;
}

export interface RobotCommandRoboMove extends RobotPosition {
    type: ROBOT_COMMAND_TYPE.ROBO_MOVE;
    [ROBOT_LINE_NUMBER]?: number;
    crashed?: true;
}

type RobotCommandGetStatusType =
    | ROBOT_COMMAND_TYPE.TEMP_OF_CELL
    | ROBOT_COMMAND_TYPE.COLOR_OF_CELL
    | ROBOT_COMMAND_TYPE.GET_CELL_TEMP // почему-то вызов rad_of_cell приходит так
    | ROBOT_COMMAND_TYPE.CELL_IS_CLEAN
    | ROBOT_COMMAND_TYPE.CELL_IS_FILLED
    | ROBOT_COMMAND_TYPE.FREE_FROM_LEFT
    | ROBOT_COMMAND_TYPE.FREE_FROM_RIGHT
    | ROBOT_COMMAND_TYPE.FREE_FROM_DOWN
    | ROBOT_COMMAND_TYPE.FREE_FROM_UP
    | ROBOT_COMMAND_TYPE.WALL_IS_ON_THE_LEFT
    | ROBOT_COMMAND_TYPE.WALL_IS_ON_THE_RIGHT
    | ROBOT_COMMAND_TYPE.WALL_IS_ON_THE_DOWN
    | ROBOT_COMMAND_TYPE.WALL_IS_ON_THE_UP;

// Получение информации об окружении
interface RobotCommandGetStatus {
    type: RobotCommandGetStatusType;
    status: 'type';
    [ROBOT_LINE_NUMBER]: number;
}

export interface RobotCommandFinish {
    type: ROBOT_COMMAND_TYPE.FINISH;
    msg: string;
    status: ROBOT_FINISH_STATUS;
    [ROBOT_LINE_NUMBER]: number;
}

export type RobotCommand =
    | RobotCommandBegin
    | RobotCommandCreateField
    | RobotCommandCreateWalls
    | RobotCommandSetCellType
    | RobotCommandSetCellColor
    | RobotCommandSetRequiredColor
    | RobotCommandSetTemperature
    | RobotCommandSetRadiation
    | RobotCommandCreateLabel
    | RobotCommandCreateParkingPoint
    | RobotCommandFinishInit
    | RobotCommandRoboMove
    | RobotCommandGetStatus
    | RobotCommandFinish;

export enum ROBOT_TABS {
    CODE = 'code',
    INFO = 'info',
}

export interface RobotSampleErrors {
    exception_type: string;
    exception_message: string;
    exception_line: number;
}

export interface RobotAttemptItem {
    check_details: {
        sample: RobotCommand[];
        verdict?: RobotCommandFinish;
        errors?: RobotSampleErrors;
    };
    mistake_count?: number;
    answer_status?: boolean;
}

export interface RobotRunCodeResponse {
    // is_visible: boolean;
    attempt: RobotAttempt;
    // custom_answer: boolean;
    // date_updated: number;
    // markup: RobotProblem['markup'];
    // problem_themes: any[];
    // max_points: number;
    // owner: number;
    // type: string;
    // id: number;
    // resources: Resources;
    // subject: string;
}
